'use client';

import {
  SignalStage,
  SignalStatus,
  type BlockedReasonType,
  type ConfirmedVulnerabilityFindingStateType,
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import { token } from '@terra/ui-styled-system';
import {
  AlertCircleIcon,
  CancelCircleHalfDotIcon,
  Clock01Icon,
  EqualSignCircleIcon,
  HelpCircleIcon,
  MinusSignCircleIcon,
  UnavailableIcon,
  type TerraIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Badge, Tooltip } from '../..';

type SignalStatusBadgeConfig = {
  icon: TerraIcon;
  iconColor: string;
};
const SIGNAL_STATUS_BADGE_CONFIG: Record<
  SignalStatusType,
  SignalStatusBadgeConfig
> = {
  [SignalStatus.POTENTIAL_VULNERABILITY]: {
    icon: HelpCircleIcon,
    iconColor: token('colors.fg.vulnerability.hot'),
  },
  [SignalStatus.BLOCKED]: {
    icon: UnavailableIcon,
    iconColor: token('colors.fg.severity.high'),
  },
  [SignalStatus.CONFIRMED_VULNERABILITY]: {
    icon: AlertCircleIcon,
    iconColor: token('colors.fg.vulnerability.hot'),
  },
  [SignalStatus.DISPROVEN_VULNERABILITY]: {
    icon: MinusSignCircleIcon,
    iconColor: token('colors.fg.vulnerability.hot'),
  },
  [SignalStatus.DUPLICATE]: {
    icon: EqualSignCircleIcon,
    iconColor: token('colors.fg.status.duplicate'),
  },
  [SignalStatus.PENDING_ENRICHMENT]: {
    icon: Clock01Icon,
    iconColor: token('colors.fg.default'),
  },
  [SignalStatus.FAILED_ENRICHMENT]: {
    icon: CancelCircleHalfDotIcon,
    iconColor: token('colors.fg.severity.critical'),
  },
};

export type SignalStatusBadgeProps = {
  status: SignalStatusType | null | undefined;
  stage?: SignalStageType | null;
  confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType | null;
  blockedReason?: BlockedReasonType | null;
  tooltipTitle?: string;
};
export const SignalStatusBadge = ({
  status,
  stage,
  confirmedVulnerabilityFindingState,
  blockedReason,
  tooltipTitle,
}: SignalStatusBadgeProps) => {
  const tCommon = useTranslations('designSystem.common');
  const tStatus = useTranslations('designSystem.signals.lifecycle.status');

  if (!status) return null;
  const statusConfig = SIGNAL_STATUS_BADGE_CONFIG[status];

  if (!statusConfig) return null;
  const StatusIcon = statusConfig.icon;
  const statusLabel = tStatus(status);
  const shouldShowFindingState =
    status === SignalStatus.CONFIRMED_VULNERABILITY &&
    stage === SignalStage.TESTED &&
    confirmedVulnerabilityFindingState;
  const shouldShowBlockedReason =
    status === SignalStatus.BLOCKED && blockedReason;
  const label = shouldShowFindingState
    ? `${statusLabel} (${tCommon(confirmedVulnerabilityFindingState)})`
    : shouldShowBlockedReason
      ? `${statusLabel} - ${tStatus(`blockedReason.${blockedReason}`)}`
      : statusLabel;

  const tooltip = tooltipTitle?.trim() ? tooltipTitle : label;

  return (
    <Tooltip title={tooltip} positioning={{ strategy: 'fixed' }}>
      <Badge
        variant="outline"
        size="sm"
        bg={token('colors.bg.neutral.default')}
        borderColor={token('colors.border.neutral.muted')}
        icon={<StatusIcon size={16} color={statusConfig.iconColor} />}
      >
        {label}
      </Badge>
    </Tooltip>
  );
};
